import React, { type FC, useState } from 'react'
import type {
  BetStatus,
  DragItem,
  PersonRole,
  ProofPoint,
  Stage,
} from '../domain/Bet.domain'
import { useCreateBet } from '../repositories/useCreateBet'
import { useCreateCategory } from '../repositories/useCreateCategory'
import { useCreatePerson } from '../repositories/useCreatePerson'
import { useDeleteCategory } from '../repositories/useDeleteCategory'
import { useDeletePerson } from '../repositories/useDeletePerson'
import { useGetBets } from '../repositories/useGetBets'
import { useGetCategories } from '../repositories/useGetCategories'
import { useGetPersons } from '../repositories/useGetPersons'
import { useUpdateCategory } from '../repositories/useUpdateCategory'
import { useUpdateProofPoint } from '../repositories/useUpdateProofPoint'
import { BoardView } from './BoardView'
import { DashboardView } from './DashboardView'

const ROLES: PersonRole[] = ['Head of', 'Employee']

type Tab = 'board' | 'dashboard'

const STATUSES: BetStatus[] = [
  'Doubling down',
  'Evaluating',
  'Exploring',
  'Pivoting',
  'Killing',
]

export const ScoreboardPage: FC = () => {
  const [tab, setTab] = useState<Tab>('board')
  const [showNewBet, setShowNewBet] = useState(false)
  const [newThesis, setNewThesis] = useState('')
  const [newOwnerId, setNewOwnerId] = useState('')
  const [newCategoryId, setNewCategoryId] = useState('')
  const [newStatus, setNewStatus] = useState<BetStatus>('Exploring')
  const [showPeople, setShowPeople] = useState(false)
  const [showCategories, setShowCategories] = useState(false)
  const [newPersonName, setNewPersonName] = useState('')
  const [newPersonRole, setNewPersonRole] = useState<PersonRole>('Head of')
  const [newCatName, setNewCatName] = useState('')
  const [editingCatId, setEditingCatId] = useState<string | null>(null)
  const [editingCatName, setEditingCatName] = useState('')

  const { data: bets = [], isLoading, isError } = useGetBets()
  const { data: persons = [] } = useGetPersons()
  const { data: categories = [] } = useGetCategories()
  const { mutate: updateProofPoint } = useUpdateProofPoint()
  const { mutate: createBet, isPending: isCreating } = useCreateBet()
  const { mutate: createPerson, isPending: isCreatingPerson } =
    useCreatePerson()
  const { mutate: deletePerson } = useDeletePerson()
  const { mutate: createCategory, isPending: isCreatingCat } =
    useCreateCategory()
  const { mutate: updateCategory } = useUpdateCategory()
  const { mutate: deleteCategory } = useDeleteCategory()

  const handleCreatePerson = () => {
    if (!newPersonName.trim()) return
    createPerson(
      { name: newPersonName.trim(), role: newPersonRole },
      {
        onSuccess: () => {
          setNewPersonName('')
          setNewPersonRole('Head of')
        },
      }
    )
  }

  const handleChangeProof = (
    betId: string,
    stage: Stage,
    proofId: string,
    updated: ProofPoint
  ) => {
    updateProofPoint({
      id: proofId,
      betId,
      fromStage: stage,
      text: updated.text,
      score: updated.score,
      date: updated.date,
      ownerId: updated.ownerId,
    })
  }

  const handleMoveProof = (item: DragItem, toStage: Stage) => {
    updateProofPoint({
      id: item.proofId,
      betId: item.betId,
      fromStage: item.fromStage,
      stage: toStage,
    })
  }

  const handleCreateBet = () => {
    if (!newThesis.trim() || !newOwnerId || !newCategoryId) return
    createBet(
      {
        thesis: newThesis.trim(),
        ownerId: newOwnerId,
        categoryId: newCategoryId,
        status: newStatus,
      },
      {
        onSuccess: () => {
          setShowNewBet(false)
          setNewThesis('')
          setNewOwnerId('')
          setNewCategoryId('')
          setNewStatus('Exploring')
        },
      }
    )
  }

  return (
    <div
      style={{
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        maxWidth: 960,
        margin: '0 auto',
        padding: 24,
        color: '#111827',
      }}
    >
      {/* Header */}
      <div
        style={{
          marginBottom: 20,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 700,
              margin: 0,
              letterSpacing: -0.5,
            }}
          >
            The Scoreboard
          </h1>
          <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: 14 }}>
            Bets · Proof Points · Pipeline · Conviction
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            onClick={() => {
              setShowCategories((v) => !v)
              if (!showCategories) {
                setShowPeople(false)
                setShowNewBet(false)
              }
            }}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              background: showCategories ? '#f3f4f6' : '#fff',
              color: '#374151',
              whiteSpace: 'nowrap',
            }}
          >
            {showCategories ? 'Close' : 'Categories'}
          </button>
          <button
            onClick={() => {
              setShowPeople((v) => !v)
              if (!showPeople) {
                setShowNewBet(false)
                setShowCategories(false)
              }
            }}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              background: showPeople ? '#f3f4f6' : '#fff',
              color: '#374151',
              whiteSpace: 'nowrap',
            }}
          >
            {showPeople ? 'Close' : 'People'}
          </button>
          <button
            onClick={() => {
              setShowNewBet((v) => !v)
              if (!showNewBet) {
                setShowPeople(false)
                setShowCategories(false)
              }
            }}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: 'none',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              background: '#111827',
              color: '#fff',
              whiteSpace: 'nowrap',
            }}
          >
            {showNewBet ? 'Cancel' : '+ New Bet'}
          </button>
        </div>
      </div>

      {/* People management */}
      {showPeople && (
        <div
          style={{
            marginBottom: 20,
            padding: 16,
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            background: '#f9fafb',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
            People
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              placeholder="Name (e.g. Jane Doe)"
              value={newPersonName}
              onChange={(e) => setNewPersonName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreatePerson()
              }}
              style={{ ...inputStyle, flex: 1, minWidth: 160, width: 'auto' }}
            />
            <select
              value={newPersonRole}
              onChange={(e) => setNewPersonRole(e.target.value as PersonRole)}
              style={selectStyle}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <button
              onClick={handleCreatePerson}
              disabled={isCreatingPerson || !newPersonName.trim()}
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                background: '#6366f1',
                color: '#fff',
                opacity: isCreatingPerson || !newPersonName.trim() ? 0.5 : 1,
              }}
            >
              {isCreatingPerson ? 'Adding…' : '+ Add'}
            </button>
          </div>
          {persons.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                marginTop: 4,
              }}
            >
              {persons.map((p) => (
                <span
                  key={p.id}
                  style={{
                    fontSize: 12,
                    padding: '4px 10px',
                    borderRadius: 16,
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {p.name}{' '}
                  <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                    {p.role}
                  </span>
                  <button
                    onClick={() => deletePerson(p.id)}
                    title={`Remove ${p.name}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#d1d5db',
                      fontSize: 13,
                      lineHeight: 1,
                      padding: '0 2px',
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Categories management */}
      {showCategories && (
        <div
          style={{
            marginBottom: 20,
            padding: 16,
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            background: '#f9fafb',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
            Categories
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              placeholder="Category name (e.g. Market)"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newCatName.trim()) {
                  createCategory(
                    { name: newCatName.trim() },
                    { onSuccess: () => setNewCatName('') }
                  )
                }
              }}
              style={{ ...inputStyle, flex: 1, minWidth: 160, width: 'auto' }}
            />
            <button
              onClick={() => {
                if (!newCatName.trim()) return
                createCategory(
                  { name: newCatName.trim() },
                  { onSuccess: () => setNewCatName('') }
                )
              }}
              disabled={isCreatingCat || !newCatName.trim()}
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                background: '#6366f1',
                color: '#fff',
                opacity: isCreatingCat || !newCatName.trim() ? 0.5 : 1,
              }}
            >
              {isCreatingCat ? 'Adding…' : '+ Add'}
            </button>
          </div>
          {categories.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                marginTop: 4,
              }}
            >
              {categories.map((cat) => (
                <span
                  key={cat.id}
                  style={{
                    fontSize: 12,
                    padding: '4px 10px',
                    borderRadius: 16,
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {editingCatId === cat.id ? (
                    <input
                      value={editingCatName}
                      onChange={(e) => setEditingCatName(e.target.value)}
                      onBlur={() => {
                        const trimmed = editingCatName.trim()
                        if (trimmed && trimmed !== cat.name) {
                          updateCategory({ id: cat.id, name: trimmed })
                        }
                        setEditingCatId(null)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter')
                          (e.target as HTMLInputElement).blur()
                        if (e.key === 'Escape') setEditingCatId(null)
                      }}
                      autoFocus
                      style={{
                        fontSize: 12,
                        border: '1px solid #d1d5db',
                        borderRadius: 4,
                        padding: '0 4px',
                        outline: 'none',
                        width: 80,
                      }}
                    />
                  ) : (
                    <span
                      onClick={() => {
                        setEditingCatId(cat.id)
                        setEditingCatName(cat.name)
                      }}
                      style={{ cursor: 'pointer' }}
                      title="Click to rename"
                    >
                      {cat.name}
                    </span>
                  )}
                  <button
                    onClick={() => deleteCategory(cat.id)}
                    title={`Remove ${cat.name}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#d1d5db',
                      fontSize: 13,
                      lineHeight: 1,
                      padding: '0 2px',
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New Bet form */}
      {showNewBet && (
        <div
          style={{
            marginBottom: 20,
            padding: 16,
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            background: '#f9fafb',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <input
            placeholder="Thesis (e.g. Self-serve drives 3x activation)"
            value={newThesis}
            onChange={(e) => setNewThesis(e.target.value)}
            style={inputStyle}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={newOwnerId}
              onChange={(e) => setNewOwnerId(e.target.value)}
              style={{ ...selectStyle, flex: 1, minWidth: 140 }}
            >
              <option value="">Select owner…</option>
              {persons
                .filter((p) => p.role === 'Head of')
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
            <select
              value={newCategoryId}
              onChange={(e) => setNewCategoryId(e.target.value)}
              style={{ ...selectStyle, flex: 1, minWidth: 140 }}
            >
              <option value="">Select category…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as BetStatus)}
              style={selectStyle}
            >
              {STATUSES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <button
              onClick={handleCreateBet}
              disabled={
                isCreating || !newThesis.trim() || !newOwnerId || !newCategoryId
              }
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                background: '#10b981',
                color: '#fff',
                opacity:
                  isCreating ||
                  !newThesis.trim() ||
                  !newOwnerId ||
                  !newCategoryId
                    ? 0.5
                    : 1,
              }}
            >
              {isCreating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {(['board', 'dashboard'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '7px 18px',
              borderRadius: 8,
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              background: tab === t ? '#111827' : '#f3f4f6',
              color: tab === t ? '#fff' : '#6b7280',
            }}
          >
            {t === 'board' ? 'Board' : 'Dashboard'}
          </button>
        ))}
      </div>

      {isLoading && (
        <div
          style={{
            color: '#9ca3af',
            fontSize: 14,
            padding: '40px 0',
            textAlign: 'center',
          }}
        >
          Loading bets…
        </div>
      )}

      {isError && (
        <div style={{ color: '#ef4444', fontSize: 14, padding: '20px 0' }}>
          Failed to load bets. Make sure the database is reachable.
        </div>
      )}

      {!isLoading && !isError && tab === 'dashboard' && (
        <DashboardView bets={bets} />
      )}

      {!isLoading && !isError && tab === 'board' && (
        <BoardView
          bets={bets}
          onChangeProof={handleChangeProof}
          onMoveProof={handleMoveProof}
        />
      )}

      {/* Legend */}
      <div
        style={{
          marginTop: 24,
          padding: 14,
          background: '#f9fafb',
          borderRadius: 10,
          border: '1px solid #e5e7eb',
          fontSize: 12,
          color: '#9ca3af',
          lineHeight: 1.7,
        }}
      >
        <strong style={{ color: '#6b7280' }}>Scoring:</strong> Each proof point
        gets a 1–5 score. <span style={{ color: '#ef4444' }}>Red (1–2)</span> =
        weak/negative signal.{' '}
        <span style={{ color: '#f59e0b' }}>Yellow (3)</span> =
        neutral/inconclusive.{' '}
        <span style={{ color: '#10b981' }}>Green (4–5)</span> = strong positive
        signal. The bet's traffic light is the average of its proof points.
        Conviction trend compares last 30d delivered scores vs older ones. Drag
        proof point cards between stages to move them along the pipeline.
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  fontSize: 13,
  width: '100%',
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box',
}

const selectStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  fontSize: 13,
  background: '#fff',
  cursor: 'pointer',
}
